import { useEffect } from 'react';

import { PolicyDeniedView } from '@/components/common/PolicyDeniedView';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { orgResource } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';
import { useGetFactoriesQuery } from '@/features/agents/api/agentsApi';
import type { AgentTemplateWizardStepType, WizardStepProps } from '@/features/agents/types';
import { BoundAgentNotice } from '../components/BoundAgentNotice';
import { useListNumbersQuery } from '../api/numbersApi';
import { NUMBERS_ACTIONS, isBindNumberStepValue } from '../types';
import type { PhoneNumberView } from '../types';

/**
 * `bind-number` wizard step renderer (design §12.2, P2 §14.5 contract).
 * Contributed by the numbers feature; picked up by the P2 glob step registry
 * with zero edits to P2 files. The stored value (`{ numberId }`) is an
 * INTENT executed post-create by the P2 §15.3 deferred-attach sequence
 * (`POST /v1/voice-bridge/numbers/:id/bind`) — no bind happens while the
 * factory does not exist yet.
 *
 * Verbatim P2 empty state: when the org has no numbers the step shows the
 * admin-path copy with NO create affordance (the wizard never cross-
 * navigates into the registry — P2 §11.2 stance, immutable).
 */
function BindNumberStep({ step, value, onChange, errors, onErrors, orgId }: WizardStepProps) {
  const canRead = useCan(NUMBERS_ACTIONS.read, orgResource(orgId));
  const numbersQuery = useListNumbersQuery(
    { page: 1, limit: 100 },
    { skip: canRead.allowed !== true },
  );
  const factoriesQuery = useGetFactoriesQuery({ orgId }, { skip: canRead.allowed !== true });

  const numbers: PhoneNumberView[] = numbersQuery.data?.data ?? [];
  // Client-side ordering (design §12.2/A4): active first, then number
  // ascending. Inactive numbers stay selectable — the server rejects neither.
  const ordered = [...numbers].sort((a, b) =>
    a.isActive === b.isActive ? a.number.localeCompare(b.number) : a.isActive ? -1 : 1,
  );
  const active = ordered.filter((n) => n.isActive);
  const inactive = ordered.filter((n) => !n.isActive);

  const selected = isBindNumberStepValue(value)
    ? numbers.find((number) => number.id === value.numberId)
    : undefined;

  const loadedEmpty = canRead.allowed === true && !numbersQuery.isLoading && numbers.length === 0;
  const missing = step.required && !isBindNumberStepValue(value);

  // Continue gating via the frame's step-errors contract (see the forwarding
  // step header for why the tooltip copy lands inline instead).
  useEffect(() => {
    if (step.required && loadedEmpty) {
      onErrors({ number: 'A phone number is required. Ask your administrator to allocate one.' });
    } else if (missing) {
      onErrors({ number: 'Select a phone number to continue.' });
    } else {
      onErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onErrors identity
    // changes per frame render; inputs are the stable derived flags.
  }, [step.required, loadedEmpty, missing]);

  if (canRead.allowed === false) {
    return <PolicyDeniedView />;
  }

  if (numbersQuery.isError) {
    return (
      <QueryErrorView
        error={numbersQuery.error}
        label="numbers"
        onRetry={() => void numbersQuery.refetch()}
      />
    );
  }

  if (loadedEmpty) {
    return (
      <EmptyState
        title="No phone numbers"
        description="Ask your administrator to allocate a number for this organization."
      />
    );
  }

  const boundAgentName = selected?.factoryId
    ? (factoriesQuery.data ?? []).find((factory) => factory.id === selected.factoryId)?.name
    : undefined;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="bind-number-select">Phone number</Label>
        {numbersQuery.isLoading ? (
          <LoadingRows label="Loading numbers…" columns={2} rows={3} />
        ) : (
          <>
            <Select value={selected?.id ?? ''} onValueChange={(numberId) => onChange({ numberId })}>
              <SelectTrigger id="bind-number-select" className="max-w-sm">
                <SelectValue placeholder="Select a phone number" />
              </SelectTrigger>
              <SelectContent>
                {active.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Active</SelectLabel>
                    {active.map((number) => (
                      <SelectItem key={number.id} value={number.id}>
                        <span className="font-mono">{number.number}</span>
                        {number.label ? ` — ${number.label}` : ''}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
                {active.length > 0 && inactive.length > 0 ? <SelectSeparator /> : null}
                {inactive.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Inactive</SelectLabel>
                    {inactive.map((number) => (
                      <SelectItem key={number.id} value={number.id}>
                        <span className="font-mono">{number.number}</span>
                        {number.label ? ` — ${number.label}` : ''} (inactive)
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Calls to this number forward to the agent.
            </p>
            <p className="text-sm text-muted-foreground">
              Inactive numbers won't receive calls until activated.
            </p>
          </>
        )}
        {errors.number ? (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
          >
            {errors.number}
          </p>
        ) : null}
      </div>

      {/* Same notice pattern and copy stem as the bind dialog §9 (H3, audit #1). */}
      {selected?.factoryId ? (
        <BoundAgentNotice>
          Currently bound to {boundAgentName ?? selected.factoryId}. Selecting it here moves it to
          this agent.
        </BoundAgentNotice>
      ) : null}
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'bind-number';
export const component = BindNumberStep;
